import { normalizeEmailValueForStorage } from 'utilities/Email';
import { normalizeEnumOptions, normalizeEnumOptionsSortMode } from 'utilities/EnumOptions';
import { normalizeMeasurementDefaultUnit, normalizeMeasurementOptions } from 'utilities/Measurement';
import { formatPhoneValueForInput, normalizePhoneValueForStorage } from 'utilities/Phone';

import { DEFAULT_LABELS_BY_TYPE, ENUM_OPTIONS_SORT_MODE_MANUAL } from './constants';

export const normalizeOptionsForType = (options, type) => {
    if (type === 'enum' || type === 'multiselect') return normalizeEnumOptions(options);
    if (type === 'measurement') return normalizeMeasurementOptions(options);
    return options;
};

const isEnumLikeType = (type) => type === 'enum' || type === 'multiselect';

const isOptionsDrivenType = (type) => isEnumLikeType(type) || type === 'measurement';

const normalizePercentage = (rawValue) => {
    const parsed = Number(rawValue);
    if (!Number.isFinite(parsed)) return '';
    if (parsed < 0) return 0;
    if (parsed > 100) return 100;
    return Math.round(parsed);
};

const normalizeSnapTo = (rawValue) => {
    const parsed = Number(rawValue);
    if (!Number.isFinite(parsed)) return 5;
    const rounded = Math.round(parsed);
    return rounded > 0 ? rounded : 5;
};

export const patchDraftWithRules = ({ prevDraft, patch, isEditing }) => {
    const next = { ...prevDraft, ...patch };
    const isNextMeasurementType = next.type === 'measurement';
    const isNextOptionsType = isOptionsDrivenType(next.type);

    if (patch.type) {
        const nextType = patch.type;
        const nextTypeIsEnumLike = isEnumLikeType(nextType);

        if (!isOptionsDrivenType(nextType)) {
            next.options = [];
            next.enumOptionsSort = undefined;
        } else {
            next.options = normalizeOptionsForType(next.options, nextType);
            next.enumOptionsSort = nextTypeIsEnumLike && nextType === 'enum'
                ? normalizeEnumOptionsSortMode(next.enumOptionsSort || ENUM_OPTIONS_SORT_MODE_MANUAL)
                : undefined;
        }

        if (nextType === 'boolean' && typeof next.default !== 'boolean') next.default = false;
        if (nextType !== 'boolean' && typeof next.default === 'boolean') next.default = '';

        if (nextType === 'tags' || nextType === 'people' || nextType === 'checklist' || nextType === 'multiselect') {
            next.default = undefined;
        } else if (nextType === 'enum' && Array.isArray(next.default)) {
            next.default = undefined;
        } else if (nextType === 'measurement') {
            next.default = undefined;
        }

        if (nextType !== 'nodelist') {
            next.targetType = '';
        }

        if (nextType === 'percentage') {
            next.snapTo = normalizeSnapTo(next.snapTo);
        } else {
            next.snapTo = undefined;
        }

        if (nextType === 'measurement') {
            next.defaultUnit = normalizeMeasurementDefaultUnit(next.defaultUnit, next.options);
        } else {
            next.defaultUnit = '';
        }

        if (!isEditing) {
            const currentLabel = typeof next.label === 'string' ? next.label.trim() : '';
            const defaultLabel = DEFAULT_LABELS_BY_TYPE[nextType];
            if (!currentLabel && defaultLabel) {
                next.label = defaultLabel;
            }
        }
    }

    if (isNextOptionsType) {
        next.options = normalizeOptionsForType(next.options, next.type);
        next.enumOptionsSort = next.type === 'enum'
            ? normalizeEnumOptionsSortMode(next.enumOptionsSort || ENUM_OPTIONS_SORT_MODE_MANUAL)
            : undefined;
    } else if (Object.prototype.hasOwnProperty.call(next, 'enumOptionsSort')) {
        next.enumOptionsSort = undefined;
    }

    if (isNextMeasurementType) {
        next.defaultUnit = normalizeMeasurementDefaultUnit(next.defaultUnit, next.options);
    } else if (Object.prototype.hasOwnProperty.call(next, 'defaultUnit')) {
        next.defaultUnit = '';
    }

    if (next.type === 'percentage' && patch.snapTo !== undefined) {
        next.snapTo = normalizeSnapTo(patch.snapTo);
    }

    return next;
};

export const buildDraftFromField = (field) => {
    const type = field?.type || 'string';
    const options = normalizeOptionsForType(field?.options || [], type);
    const rawDefault = field?.default;

    let nextDefault = rawDefault;
    if (type === 'boolean') {
        nextDefault = !!rawDefault;
    } else if (type === 'number' || type === 'percentage') {
        nextDefault = rawDefault === undefined || rawDefault === null ? '' : rawDefault;
    } else if (type === 'enum') {
        if (rawDefault === undefined || rawDefault === null) {
            nextDefault = '';
        } else if (Array.isArray(rawDefault)) {
            nextDefault = '';
        } else {
            nextDefault = String(rawDefault);
        }
    } else if (type === 'multiselect') {
        nextDefault = undefined;
    } else if (type === 'phone') {
        nextDefault = rawDefault === undefined || rawDefault === null
            ? ''
            : formatPhoneValueForInput(rawDefault);
    } else if (type === 'email') {
        nextDefault = rawDefault === undefined || rawDefault === null
            ? ''
            : normalizeEmailValueForStorage(rawDefault);
    } else if (type === 'tags' || type === 'people' || type === 'checklist') {
        nextDefault = undefined;
    } else {
        nextDefault = rawDefault === undefined || rawDefault === null ? '' : rawDefault;
    }

    return {
        label: field?.label || '',
        fieldDescription: String(field?.fieldDescription || '').trim(),
        type,
        targetType: type === 'nodelist' ? String(field?.targetType || '').trim() : '',
        default: nextDefault,
        defaultUnit: type === 'measurement'
            ? normalizeMeasurementDefaultUnit(field?.defaultUnit, options)
            : '',
        options,
        enumOptionsSort: type === 'enum'
            ? normalizeEnumOptionsSortMode(field?.enumOptionsSort || ENUM_OPTIONS_SORT_MODE_MANUAL)
            : undefined,
        snapTo: type === 'percentage' ? normalizeSnapTo(field?.snapTo) : undefined,
    };
};

export const buildNextFieldFromDraft = (draft) => {
    const isEnumType = draft.type === 'enum';
    const isMultiSelectType = draft.type === 'multiselect';
    const isTagsType = draft.type === 'tags';
    const isPeopleType = draft.type === 'people';
    const isChecklistType = draft.type === 'checklist';
    const isMeasurementType = draft.type === 'measurement';
    const normalizedMeasurementOptions = isMeasurementType
        ? normalizeMeasurementOptions(draft.options)
        : undefined;

    const nextField = {
        label: draft.label.trim(),
        fieldDescription: String(draft.fieldDescription || '').trim(),
        type: draft.type,
        targetType: draft.type === 'nodelist' ? String(draft.targetType || '').trim() : undefined,
        options: isEnumType || isMultiSelectType
            ? normalizeEnumOptions(draft.options)
            : normalizedMeasurementOptions,
        default: draft.default,
        defaultUnit: isMeasurementType
            ? normalizeMeasurementDefaultUnit(draft.defaultUnit, normalizedMeasurementOptions)
            : undefined,
    };

    if (draft.type === 'number') {
        const raw = typeof draft.default === 'string' ? draft.default.trim() : '';
        nextField.default = raw === '' ? '' : Number(raw);
    }

    if (draft.type === 'percentage') {
        const raw = typeof draft.default === 'string' ? draft.default.trim() : draft.default;
        if (raw === '') {
            nextField.default = '';
        } else {
            nextField.default = normalizePercentage(raw);
        }
        nextField.snapTo = normalizeSnapTo(draft.snapTo);
    }

    if (draft.type === 'enum') {
        nextField.default = draft.default === '' ? undefined : draft.default;
        nextField.enumOptionsSort = normalizeEnumOptionsSortMode(draft.enumOptionsSort || ENUM_OPTIONS_SORT_MODE_MANUAL);
    } else {
        delete nextField.enumOptionsSort;
    }

    if (draft.type === 'boolean') {
        nextField.default = !!draft.default;
    }

    if (draft.type === 'phone') {
        nextField.default = normalizePhoneValueForStorage(draft.default);
    }

    if (draft.type === 'email') {
        nextField.default = normalizeEmailValueForStorage(draft.default);
    }

    if (isTagsType || isPeopleType || isChecklistType || isMultiSelectType || isMeasurementType) {
        nextField.default = undefined;
    }

    if (!isMeasurementType) {
        delete nextField.defaultUnit;
    }

    if (!nextField.fieldDescription) {
        delete nextField.fieldDescription;
    }

    if (draft.type !== 'nodelist') {
        delete nextField.targetType;
    }

    return nextField;
};
